import React, { useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  ClickAwayListener,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Popper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckIcon from "@mui/icons-material/Check";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useOwnDivision } from "../../../hooks/useOwnDivision";
import { divisionOptionValue, findDivisionOptionByStableIdentity } from "../../../utils/divisionOption";
import { MentionUser } from "../WeeklyThreadTypes";
import { displayUserInitial, isJapaneseText, normEmail, stripEmailDomain, toHiragana } from "../WeeklyThreadUtils";


// 選択ポップアップに表示するユーザ行（丸アイコン＋氏名）
const UserRow: React.FC<{
  user: MentionUser;
  selected?: boolean;   // 返信先に選択済みか（新規返信先の候補のみ）
  onClick?: () => void;
  onRemove?: () => void;
}> = ({ user, selected, onClick, onRemove }) => (
  <Box
    onClick={onClick}
    aria-pressed={onClick ? Boolean(selected) : undefined}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      px: 1,
      py: 0.75,
      borderRadius: 1,
      cursor: onClick ? "pointer" : "default",   // 新規返信先用 onClick が渡された場合のみ押せるCSSを表示
      // 選択済みは薄い primary 色の背景にする
      bgcolor: selected ? (theme) => alpha(theme.palette.primary.main, 0.08) : undefined,
      "&:hover": onClick
        ? {
            bgcolor: selected
              ? (theme) => alpha(theme.palette.primary.main, 0.16)
              : "action.hover",
          }
        : undefined,
    }}
  >
    <Avatar
      sx={{
        width: 28,
        height: 28,
        fontSize: 13,
        bgcolor: selected ? "primary.main" : "grey.200",
        color: selected ? "primary.contrastText" : "text.secondary",
      }}
    >
      {/* 選択済みは頭文字の代わりにチェックを表示 */}
      {selected ? <CheckIcon sx={{ fontSize: 18 }} /> : displayUserInitial(user.display)}
    </Avatar>
    <Box sx={{ minWidth: 0, flex: 1 }}> {/* 長い表示名対策 */}
      <Typography variant="body2" noWrap>
        {user.display}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        noWrap
        component="div"
      >
        {user.email}
      </Typography>
    </Box>
    {
      onRemove && (
        <IconButton
          aria-label={`${user.display}を返信先から外す`}
          sx={{ p: 0.5 }}
          onClick={(e) => {
            e.stopPropagation();  // 行のonClickに伝播しないように入れておく
            onRemove();
          }}
        >
          <CancelOutlinedIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )
    }
  </Box>
);

// 新規返信先候補一覧（大量件数対策：仮想スクロールで表示中の行だけ描画する）
// 2列表示のため、2件ずつを1行として仮想化する
const CANDIDATE_COLUMNS = 2;   // 列数
const CandidateList: React.FC<{
  candidates: MentionUser[];
  selectedEmails: Set<string>;   // 返信先に選択済みのメールアドレス
  onSelect: (user: MentionUser) => void;
  onDeselect: (user: MentionUser) => void;
  selectedDepartment: string;
  deferredSearchInput: string;
  loading?: boolean;
  loadError?: boolean;
}> = ({ candidates, selectedEmails, onSelect, onDeselect, selectedDepartment, deferredSearchInput, loading, loadError }) => {

  const scrollRef = useRef<HTMLDivElement>(null);
  const rowCount = Math.ceil(candidates.length / CANDIDATE_COLUMNS);

  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 52,   // UserRow 1行分の高さ（目安。実際の高さは measureElement で計測）
    overscan: 8,
  });

  // 絞り込み条件が変わったら先頭に戻す
  useEffect(() => {
    virtualizer.scrollToOffset(0);
  }, [selectedDepartment, deferredSearchInput, virtualizer]);

  return (
    <Box
      ref={scrollRef}
      sx={{
        overflowY: "auto",
        flex: 1,   // Paperの残りの高さを埋める
        minHeight: 0,
      }}
    >
      {
        loading ? (
          // 読み込み中は「該当なし」と区別して表示する
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ py: 2 }}>
            <CircularProgress size={14} />
            <Typography variant="caption" color="text.secondary">
              ユーザー一覧を読み込み中…
            </Typography>
          </Stack>
        ) : loadError ? (
          // 取得失敗時は「該当なし」と区別して表示する（再読み込みはページ側の警告から行う）
          <Typography
            variant="caption"
            color="error"
            sx={{ display: "block", py: 2, textAlign: "center" }}
          >
            ユーザー一覧の取得に失敗しました
          </Typography>
        ) : (candidates.length === 0) ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", py: 2, textAlign: "center" }}
          >
            該当するユーザーがいません
          </Typography>
        ) : (
          <Box sx={{ height: virtualizer.getTotalSize(), position: "relative" }}>
            {
              virtualizer.getVirtualItems().map((vr) => (
                <Box
                  key={vr.key}
                  data-index={vr.index}
                  ref={virtualizer.measureElement}
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    transform: `translateY(${vr.start}px)`,
                    display: "grid",
                    gridTemplateColumns: `repeat(${CANDIDATE_COLUMNS}, minmax(0, 1fr))`,
                    columnGap: 2,
                  }}
                >
                  {
                    candidates
                      .slice(vr.index * CANDIDATE_COLUMNS, (vr.index + 1) * CANDIDATE_COLUMNS)
                      .map((u) => {
                        // 選択済みはもう一度押すと返信先から外す
                        const selected = selectedEmails.has(normEmail(u.email));
                        return (
                          <UserRow
                            key={u.email}
                            user={u}
                            selected={selected}
                            onClick={() => (selected ? onDeselect(u) : onSelect(u))}
                          />
                        );
                      })
                  }
                </Box>
              ))
            }
          </Box>
        )
      }
    </Box>
  );
};

// 新規返信先選択 - すべての課を対象
const ALL_DEPARTMENT = { label: "すべての課", value: "" };

// ポップアップの位置・高さ調整
const POPPER_OFFSET = 6;   // アンカーとの間隔
const VIEWPORT_MARGIN = 8;   // 画面端との余白
const MIN_POPPER_HEIGHT = 240;   // 空きが少なくてもこれ以上は縮めない
const SEARCH_PAPER_HEIGHT = 480;   // 右Popperの基本の高さ

type Props = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  paperRef?: React.Ref<HTMLDivElement>;   // Popper内側の判定用

  /** 返信先 **/
  mentions: MentionUser[];   // 現在選択されている返信先
  onRemoveMention: (user: MentionUser) => void;   // 返信先から除く
  onSelectMention: (user: MentionUser) => void;   // 返信先に追加する

  history: MentionUser[];     // 履歴一覧
  historyLoadError?: boolean;   // 履歴の取得に失敗したか
  mentionCandidates: MentionUser[];   // 新規返信先候補一覧
  candidatesLoading?: boolean;   // 新規返信先候補（ユーザー一覧）の読み込み中か
  candidatesLoadError?: boolean;   // 新規返信先候補（ユーザー一覧）の取得に失敗したか
};
// 通知先候補選択ポップアップ
const MentionPickerPopper: React.FC<Props> = ({
  anchorEl,
  onClose,
  paperRef,
  mentions,
  onRemoveMention,
  history,
  historyLoadError,
  mentionCandidates,
  candidatesLoading,
  candidatesLoadError,
  onSelectMention,
}) => {

  // 左のPopper開閉状態
  const open = Boolean(anchorEl);
  // 右（新規返信先選択）のPopper開閉状態
  const [isSearchOpen, setIsSearchOpen] = useState(false);


  // *** 入力項目 ***
  // 名前・メールアドレスを入力（検索ボックス）
  const [ searchInput, setSearchInput ] = useState("");

  // 課セレクトボックスリスト一覧（SearchAreaと同じロジックで取得）
  const { divisionOptions, ownDivisionOption } = useOwnDivision();
  const departmentAutoAppliedRef = useRef(false);
  const departmentTouchedRef = useRef(false);

  // 選択可能な課の選択肢一覧（空文字は省く）
  const selectableDivisionOptions = useMemo(
    () => divisionOptions.filter((opt) => divisionOptionValue(opt).trim() !== ""),
    [divisionOptions]
  );

  const [ selectedDepartment, setSelectedDepartment] = useState(ALL_DEPARTMENT.value);

  // 課の選択値
  const selectedDivisionOption = useMemo(
    () =>
      selectedDepartment === ALL_DEPARTMENT.value
        ? null
        : findDivisionOptionByStableIdentity(selectedDepartment, divisionOptions) ?? null,
    [selectedDepartment, divisionOptions]
  );

  // 初期選択は自分の課に寄せる（1回だけ）
  useEffect(() => {
    if (departmentAutoAppliedRef.current || departmentTouchedRef.current) return;
    if (!ownDivisionOption) return;
    const nextVal = divisionOptionValue(ownDivisionOption);
    if (nextVal && nextVal !== selectedDepartment) setSelectedDepartment(nextVal);
    departmentAutoAppliedRef.current = true;
  }, [ownDivisionOption, selectedDepartment, setSelectedDepartment]);


  // *** 返信先候補関連 ***
  // 入力中の再描画を優先し、候補の絞り込みは遅延させる
  const deferredSearchInput = useDeferredValue(searchInput);

  // 候補ごとの検索用文字列・所属課を事前に作っておく（入力のたびに全件を正規化しないため）
  const candidateIndex = useMemo(() => {
    // 所属課の解決は課の値ごとに1回だけ行う
    const divisionCache = new Map<string, ReturnType<typeof findDivisionOptionByStableIdentity>>();
    const resolveDivision = (department: unknown) => {
      const key = String(department ?? "");
      if (!divisionCache.has(key)) {
        divisionCache.set(key, findDivisionOptionByStableIdentity(department, divisionOptions));
      }
      return divisionCache.get(key);
    };

    return mentionCandidates.map((u) => {
      const display = `${u.display ?? ""}`.normalize("NFKC");
      const kana = toHiragana(String(u.kana ?? "")).normalize("NFKC");
      const local = stripEmailDomain(u.email);
      const displayForSearch =
        display.toLowerCase() === u.email.toLowerCase() ? local : display;
      return {
        user: u,
        emailKey: normEmail(u.email),
        hayJa: `${toHiragana(display)} ${kana}`.toLowerCase(),
        hayEn: `${displayForSearch} ${local}`.toLowerCase(),
        division: resolveDivision(u.department),
      };
    });
  }, [mentionCandidates, divisionOptions]);

  // 新規返信先の候補一覧
  const candidates = useMemo(() => {

    // 検索語の準備
    const raw = String(deferredSearchInput ?? "").normalize("NFKC").trim();
    const q = raw.toLowerCase();
    const qJa = toHiragana(raw).toLowerCase();
    const isJa = isJapaneseText(q);

    // 選択済みのユーザも候補に残し、行側で選択状態を表示する
    // 1) 検索語あり → 検索結果 / 検索語なし → 全候補
    // 2) 課で絞り込み（全ての課ならそのまま）
    return candidateIndex
      .filter((c) => {
        if (q && !(isJa ? c.hayJa.includes(qJa) : c.hayEn.includes(q))) return false;
        if (selectedDivisionOption && c.division !== selectedDivisionOption) return false;
        return true;
      })
      .map((c) => c.user);

  }, [candidateIndex, deferredSearchInput, selectedDivisionOption]);

  // 返信先に含まれているユーザ（履歴・新規返信先候補の選択状態の表示に使う）
  const selectedEmails = useMemo(
    () => new Set(mentions.map((m) => normEmail(m.email))),
    [mentions]
  );

  // 新規返信先選択（右Popperを閉じる）
  const closeSearch = () => {
    setIsSearchOpen(false);
    setSearchInput("");
  };

  useEffect(() => {
    // 左のPopperが閉じたら右も閉じる
    if (!open) closeSearch();
  }, [open]);

  // アンカーより上の空き高さ（画面が低い環境で見切れないよう、ポップアップの高さ上限に使う）
  const [availableHeight, setAvailableHeight] = useState<number>();
  useEffect(() => {
    if (!anchorEl) return;
    const update = () => {
      const top = anchorEl.getBoundingClientRect().top;
      setAvailableHeight(Math.max(MIN_POPPER_HEIGHT, top - POPPER_OFFSET - VIEWPORT_MARGIN));
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [anchorEl]);

  // 中身の大きさが変わったら位置を再計算する（右Popperの開閉・返信先や履歴の増減）
  const popperInstanceRef = useRef<{ update: () => unknown } | null>(null);
  useEffect(() => {
    popperInstanceRef.current?.update();
  }, [isSearchOpen, mentions.length, history.length, availableHeight]);

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="top-start"
      popperRef={(instance) => { popperInstanceRef.current = instance; }}
      sx={{ zIndex: 1300 }}
      modifiers={[
        { name: "offset", options: { offset: [0, POPPER_OFFSET] } },
        { name: "flip", options: { fallbackPlacements: ["bottom-start"] } },
        { name: "preventOverflow", options: { altAxis: true, padding: VIEWPORT_MARGIN } },   // 上下どちらにも収まらない場合は画面内に押し戻す
        { name: "computeStyles", options: { gpuAcceleration: false }},   // SelectBoxのリスト位置ずれ対策でtop/left指定
      ]}
    >
      <ClickAwayListener 
        onClickAway={onClose}
        mouseEvent="onMouseDown"
        touchEvent="onTouchStart"
      >
        <Box
          ref={paperRef}
          sx={{ display: "flex", alignItems: "flex-end", gap: 1, maxHeight: availableHeight }}
        >
          {/* 左Popper：返信先・履歴・新規返信先 */}
          <Paper
            elevation={8}
            sx={{
              width: 320,
              maxHeight: availableHeight,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Box sx={{ px: 1.5, pt: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                返信先
              </Typography>
              <Divider sx={{ mt: 0.5 }} />
            </Box>
            {/* 高さが足りない場合はここを縮めてスクロールさせる */}
            <Box sx={{ px: 0.5, py: 0.5, flex: "0 1 auto", minHeight: 0, maxHeight: 260, overflowY: "auto" }}>
              {
                (mentions.length === 0) ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", px: 1, py: 1.5 }}
                  >
                    返信先が指定されていません
                  </Typography>
                ) : (
                  mentions.map((u) => (
                    <UserRow
                      key={u.email}
                      user={u}
                      onRemove={() => onRemoveMention(u)}
                    />
                  ))
                )
              }
            </Box>

            <Box sx={{ px: 1.5, pt: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <GroupAddOutlinedIcon fontSize="small" />
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  最近の返信先
                </Typography>
              </Stack>
              <Box
                sx={{
                  mt: 0.75,
                  p: 1,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1.5,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.5,
                  minHeight: 40,
                  maxHeight: 96,
                  overflowY: "auto",
                }}
              >
                {
                  historyLoadError ? (
                    <Typography variant="caption" color="error">
                      履歴の取得に失敗しました
                    </Typography>
                  ) : (history.length === 0) ? (
                    <Typography variant="caption" color="text.secondary">
                      履歴はありません
                    </Typography>
                  ) : (
                    history.map((u) => {
                      const selected = selectedEmails.has(normEmail(u.email));
                      return (
                        <Chip
                          key={u.email}
                          label={u.display}
                          title={u.email}
                          size="small"
                          color={selected ? "primary" : "default"}
                          variant={selected ? "filled" : "outlined"}
                          icon={selected ? <CheckIcon /> : undefined}
                          aria-pressed={selected}
                          onClick={() =>
                            selected ? onRemoveMention(u) : onSelectMention(u)
                          }
                          sx={{ fontSize: 11 }}
                        />
                      );
                    })
                  )
                }
              </Box>
            </Box>

            <Button
              onClick={() => setIsSearchOpen((v) => !v)}
              startIcon={<GroupAddOutlinedIcon />}
              endIcon={<ArrowForwardIcon color="primary" />}
              sx={{
                m: 1,
                justifyContent: "flex-start",
                textTransform: "none",
                color: "text.primary",
                fontWeight: 700,
                "& .MuiButton-endIcon": { ml: "auto" },
                bgcolor: isSearchOpen ? "action.selected" : undefined,
              }}
            >
              新規返信先
            </Button>
          </Paper>

          {/* 右Popper：新規返信先の検索 */}
          {
            isSearchOpen && (
              <Paper
                elevation={8}
                sx={{
                  width: 640,
                  minWidth: 400,
                  maxWidth: "calc(100vw - 360px)",
                  height: availableHeight ? Math.min(SEARCH_PAPER_HEIGHT, availableHeight) : SEARCH_PAPER_HEIGHT,
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Select
                    size="small"
                    value={selectedDepartment}
                    displayEmpty
                    onChange={(e) => {
                      departmentTouchedRef.current = true;
                      setSelectedDepartment(String(e.target.value));
                    }}
                    sx={{ minWidth: 110, fontSize: 13 }}
                  >
                    <MenuItem value={ALL_DEPARTMENT.value} sx={{ fontSize: 13 }}>
                      {ALL_DEPARTMENT.label}
                    </MenuItem>
                    {
                      selectableDivisionOptions.map((opt) => (
                        <MenuItem
                          key={`${opt.department_id ?? "dept"}-${divisionOptionValue(opt)}`}
                          value={divisionOptionValue(opt)}
                          sx={{ fontSize: 13 }}
                        >
                          {opt.label ?? opt.value}
                        </MenuItem>
                      ))
                    }
                  </Select>

                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 140,
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 0.5,
                      px: 1,
                      py: 0.5,
                      minHeight: 40,
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                      "&:focus-within": { borderColor: "primary.main" },
                    }}
                  >
                    <Box
                      component="input"
                      autoFocus
                      value={searchInput}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setSearchInput(e.target.value)
                      }
                      placeholder="名前、メールアドレスを入力"
                      sx={{
                        flex: 1,
                        minWidth: 120,
                        border: "none",
                        outline: "none",
                        fontSize: 13,
                        bgcolor: "transparent",
                        color: "text.primary",
                      }}
                    />
                  </Box>

                  <Button
                    variant="outlined"
                    size="small"
                    onClick={closeSearch}
                    sx={{ whiteSpace: "nowrap", height: 40, flexShrink: 0, }}
                  >
                    閉じる
                  </Button>
                </Stack>

                <CandidateList
                  candidates={candidates}
                  selectedEmails={selectedEmails}
                  onSelect={onSelectMention}
                  onDeselect={onRemoveMention}
                  selectedDepartment={selectedDepartment}
                  deferredSearchInput={deferredSearchInput}
                  loading={candidatesLoading}
                  loadError={candidatesLoadError}
                />
              </Paper>
            )
          }
        </Box>
      </ClickAwayListener>
    </Popper>
  );
};

export default MentionPickerPopper;
